import React, {useCallback, useEffect, useState} from 'react';
import createAxiosInstance from "../support/AxiosSupport";
import PremiumUpsell from "./PremiumUpsell";
import {usePremiumGate} from "../hooks/usePremiumGate";

interface CategoryTrend {
    categoryId: string;
    categoryName: string;
    earlierCount: number;
    recentCount: number;
    totalInPeriod: number;
    trend: 'RISING' | 'DECLINING' | 'STABLE';
}

interface WardrobeTrend {
    months: number;
    categories: CategoryTrend[];
}

interface Props {
    ownerId: string | null;
}

const TREND_META: Record<CategoryTrend['trend'], {icon: string; label: string; cls: string}> = {
    RISING: {icon: '↑', label: 'растёт', cls: 'text-emerald-600 dark:text-emerald-400'},
    DECLINING: {icon: '↓', label: 'снижается', cls: 'text-rose-600 dark:text-rose-400'},
    STABLE: {icon: '→', label: 'стабильно', cls: 'text-gray-500 dark:text-gray-400'},
};

/**
 * Самостоятельный блок трендов гардероба: какие категории пользователь приобретает активнее
 * или реже в свежей половине периода. Грузит данные сам.
 */
const CategoryTrendsSection: React.FC<Props> = ({ownerId}) => {
    const [data, setData] = useState<WardrobeTrend | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const {premiumGate, capturePremiumGate, resetPremiumGate} = usePremiumGate();

    const load = useCallback(async () => {
        setLoading(true);
        setError(false);
        resetPremiumGate();
        try {
            const api = await createAxiosInstance();
            const params = ownerId ? {ownerId} : {};
            const res = await api.get<WardrobeTrend>('/trends/categories', {params});
            setData(res.data);
        } catch (e) {
            // Тренды — premium-аналитика: на Free блок не «ломается», а объясняет,
            // что открывается тарифом (R17).
            if (!capturePremiumGate(e, 'Тренды покупок доступны в Premium')) {
                setError(true);
            }
        } finally {
            setLoading(false);
        }
    }, [ownerId, capturePremiumGate, resetPremiumGate]);

    useEffect(() => {
        load();
    }, [load]);

    if (loading) {
        return (
            <section data-testid="category-trends-loading" className="animate-pulse">
                <div className="h-5 w-40 bg-gray-200 dark:bg-ink-700 rounded mb-3"/>
                <div className="h-20 bg-gray-100 dark:bg-ink-800 rounded-xl"/>
            </section>
        );
    }

    if (premiumGate) {
        // Q15: баннер посреди вкладки должен называть, что именно за ним скрыто, — иначе
        // непонятно, за что предлагают платить.
        return (
            <PremiumUpsell
                testId="category-trends-premium"
                title={premiumGate.title || 'Тренды покупок — на тарифе PRO'}
                description={premiumGate.description
                    || 'На тарифах PRO и FAMILY здесь видно, какие категории вы покупаете активнее, '
                    + 'а какие реже, и как меняется стоимость гардероба по категориям и брендам.'}
            />
        );
    }

    // Тренды некритичны: при ошибке или отсутствии данных просто не показываем блок.
    if (error || !data || data.categories.length === 0) {
        return null;
    }

    return (
        <section data-testid="category-trends" className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Тренды покупок · за {data.months} мес.
            </h2>
            <ul className="space-y-2">
                {data.categories.map(c => {
                    const meta = TREND_META[c.trend];
                    return (
                        <li key={c.categoryId}
                            className="flex justify-between items-center rounded-xl border border-gray-200 dark:border-ink-700 p-3">
                            <span className="text-gray-900 dark:text-gray-100">{c.categoryName}</span>
                            <span className={`text-sm font-medium ${meta.cls}`} data-testid={`trend-${c.trend}`}>
                                {meta.icon} {meta.label}
                                <span className="text-gray-400 dark:text-gray-500 ml-2 font-normal">
                                    {c.earlierCount} → {c.recentCount}
                                </span>
                            </span>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
};

export default CategoryTrendsSection;
